/**
 * SOFSEK — Reconciliation Variance Dashboard (§42). Per-application diff
 * between the pushed actual state and the target model. Each variance is
 * dispositioned as Accept Actual (updates the model — full sandbox gate wiring
 * lands in Phase 5) or Re-issue (a release re-states model intent — Phase 7).
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiDb } from '../../lib/api_client';
import { useAdaptiveList, type AdaptiveQuery } from '../../lib/use_adaptive_list';
import { routedUpdate } from '../../lib/routed_writes';
import { getForensicActorId } from '../../types/auth';
import { hasAccess, type AuthSession } from '../../types/auth';
import { Modal } from '../../components/common/Modal';
import { EmptyState } from '../../components/common/EmptyState';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { useFormatDateTime } from '../../lib/format_date';
import { PageHeader } from '../../components/common/PageHeader';
import { StatStrip } from '../../components/common/StatStrip';
import { ContentCard } from '../../components/common/ContentCard';
import { RefreshCw, Search } from 'lucide-react';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';

const PAGE_CODE = 'SFK.VARIANCE.LIST';

interface CodeOpt { code: string; name: string; }
interface VarianceRow {
  variance_id: number;
  application_id: number;
  variance_type_code: string;
  variance_disposition_code: string | null;
  disposed_at: string | null;
  detail: string | null;
  created_at: string;
}

const GRID_COLS: GridColumnDef[] = [
  { key: 'application', header: 'Application', defaultWidth: 180 },
  { key: 'variance_type', header: 'Type', defaultWidth: 220 },
  { key: 'detail', header: 'Detail', defaultWidth: 260 },
  { key: 'created_at', header: 'Detected', defaultWidth: 170 },
  { key: 'disposition', header: 'Disposition', defaultWidth: 200 },
];

function tlName(list: unknown, fallback: string): string {
  const arr = (list ?? []) as { locale_code: string; display_text: string }[];
  return arr.find(x => x.locale_code === 'EN_US')?.display_text ?? fallback;
}

function VarianceInner({ session }: { session: AuthSession }) {
  const { t } = useTranslation();
  const formatDateTime = useFormatDateTime();
  const [apps, setApps] = useState<CodeOpt[]>([]);
  const [types, setTypes] = useState<CodeOpt[]>([]);
  const [dispositions, setDispositions] = useState<CodeOpt[]>([]);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [appFacet, setAppFacet] = useState<string[]>([]);
  const [typeFacet, setTypeFacet] = useState<string[]>([]);
  const [openFacet, setOpenFacet] = useState<string[]>(['OPEN']);
  const [disposing, setDisposing] = useState<VarianceRow | null>(null);

  // Small lookups load once (apps/types/dispositions resolve names + fill facets).
  const loadLookups = async () => {
    const [appRes, typeRes, dispRes] = await Promise.all([
      apiDb.from('csm_application_master').select('application_id, csm_application_master_tl ( locale_code, display_text )'),
      apiDb.from('sfk_variance_type').select('variance_type_code, sfk_variance_type_tl ( locale_code, display_text )'),
      apiDb.from('sfk_variance_disposition').select('variance_disposition_code, sfk_variance_disposition_tl ( locale_code, display_text )'),
    ]);
    const err = appRes.error ?? typeRes.error ?? dispRes.error;
    if (err) { setLookupError(err.message); return; }
    setApps(((appRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ code: String(r.application_id), name: tlName(r.csm_application_master_tl, String(r.application_id)) })));
    setTypes(((typeRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ code: r.variance_type_code as string, name: tlName(r.sfk_variance_type_tl, r.variance_type_code as string) })));
    setDispositions(((dispRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ code: r.variance_disposition_code as string, name: tlName(r.sfk_variance_disposition_tl, r.variance_disposition_code as string) })));
  };
  useEffect(() => { void loadLookups(); }, []);

  // Adaptive main list: instant client-side search under the cap, server-side above.
  const mapRow = useCallback((r: Record<string, unknown>): VarianceRow => ({
    variance_id: r.variance_id as number,
    application_id: r.application_id as number,
    variance_type_code: r.variance_type_code as string,
    variance_disposition_code: (r.variance_disposition_code ?? null) as string | null,
    disposed_at: (r.disposed_at ?? null) as string | null,
    detail: (r.detail ?? null) as string | null,
    created_at: r.created_at as string,
  }), []);
  const facets = useMemo(() => [
    { column: 'application_id', values: appFacet.map(Number) },
    { column: 'variance_type_code', values: typeFacet },
  ], [appFacet, typeFacet]);
  // Open/Disposed is a null-check on variance_disposition_code, not an .in() facet.
  const openDisposedFilter = useCallback((q: AdaptiveQuery) => {
    const open = openFacet.includes('OPEN');
    const disp = openFacet.includes('DISPOSED');
    if (open && !disp) return q.is('variance_disposition_code', null);
    if (disp && !open) return q.or('variance_disposition_code.not.is.null');
    return q;
  }, [openFacet]);
  const { rows, total, truncated, busy, error: listError, reload } =
    useAdaptiveList<Record<string, unknown>, VarianceRow>({
      table: 'sfk_variance',
      columns: 'variance_id, application_id, variance_type_code, variance_disposition_code, disposed_at, detail, created_at',
      order: { column: 'created_at', ascending: false },
      search: filter,
      searchColumns: ['detail'],
      facets,
      filter: openDisposedFilter,
      map: mapRow,
      deps: [openFacet],
    });
  const error = listError ?? lookupError;

  const appName = (id: number) => apps.find(a => a.code === String(id))?.name ?? String(id);
  const resolveNameForType = (code: string) => types.find(x => x.code === code)?.name ?? code;
  const resolveNameForDisposition = (code: string | null) => (code ? dispositions.find(x => x.code === code)?.name ?? code : t('sfk.variance.open', 'Open'));

  const detail = useExpandedRows<number>();
  // §6/§17: csm_role_page_access decides which write affordances this user is
  // offered. The server refuses the write regardless (RLS, via the Common
  // Routine) — this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  // §17.B13 — related records for a variance: the application it drifted in, the
  // variance type, and its disposition (resolved names, §17.B4).
  const buildRelatedGroups = (r: VarianceRow): RelatedChildGroup[] => [
    { key: 'application', title: t('sfk.variance.related.app', 'Application'),
      items: [{ id: r.application_id, label: appName(r.application_id) }] },
    { key: 'type', title: t('sfk.variance.related.type', 'Type'),
      items: [{ id: r.variance_type_code, label: resolveNameForType(r.variance_type_code) }] },
    { key: 'disposition', title: t('sfk.variance.related.disposition', 'Disposition'),
      items: [{ id: 'd', label: resolveNameForDisposition(r.variance_disposition_code) }] },
  ];
  const hasRelated = (_r: VarianceRow) => true;

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('sfk.variance.title', 'Reconciliation Variance Dashboard')}
          description={t('sfk.variance.subtitle', 'Per-application drift between pushed actual state and the target model.')}
          actions={
            <button onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5"><RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh', 'Refresh')}</button>
          }
        />
        <StatStrip stats={[{ value: total, label: t('common.total', 'Total') }, { value: rows.length, label: t('common.shown', 'Shown') }]} />
        {error && <div className="rounded-csm-card border border-csm-danger-border bg-csm-danger-soft px-3 py-1.5 text-csm-xs text-csm-danger">{error}</div>}
        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input type="text" value={filter} onChange={e => setFilter(e.target.value)} placeholder={t('common.filter', 'Filter…')}
                className="h-8 w-72 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none" />
            </div>
            <FacetFilter label={t('sfk.variance.facet.app', 'Application')} options={apps.map(a => ({ value: a.code, label: a.name }))} selected={appFacet} onChange={setAppFacet} />
            <FacetFilter label={t('sfk.variance.facet.type', 'Type')} options={types.map(x => ({ value: x.code, label: x.name }))} selected={typeFacet} onChange={setTypeFacet} />
            <FacetFilter label={t('sfk.variance.facet.status', 'Status')} options={[{ value: 'OPEN', label: t('sfk.variance.open', 'Open') }, { value: 'DISPOSED', label: t('sfk.variance.disposed', 'Disposed') }]} selected={openFacet} onChange={setOpenFacet} />
            <ClearFiltersButton active={filter.trim() !== '' || appFacet.length > 0 || typeFacet.length > 0 || openFacet.length !== 1} onClear={() => { setFilter(''); setAppFacet([]); setTypeFacet([]); setOpenFacet([]); }} />
            <div className="ml-auto flex items-center gap-2">
              {truncated && !busy && (
                <span className="text-csm-xs text-csm-muted">{t('common.list.truncated', 'Showing first {{n}} of {{total}} — refine your search', { n: rows.length, total })}</span>
              )}
              {busy && <span className="text-csm-xs text-csm-muted">{t('common.loading', 'Loading…')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          <div className="flex-1 overflow-auto">
            <PersonalizedGrid componentKey="grid" columns={GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="border-b border-csm-border px-1 py-1.5 text-left" style={{ width: '1%' }} aria-hidden>&nbsp;</th>
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-3 py-1.5 text-left" style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                  <th className="border-b border-csm-border px-3 py-1.5 text-left" style={{ width: '1%' }}>{t('common.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <Fragment key={r.variance_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton open={detail.isOpen(r.variance_id)} hasChildren={hasRelated(r)} onToggle={() => detail.toggle(r.variance_id)}
                        expandLabel={t('common.related.expand', 'Show related records')} collapseLabel={t('common.related.collapse', 'Hide related')} tooltip={t('common.related.tooltip', 'Show related records')} />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5" style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'application' && appName(r.application_id)}
                        {col.key === 'variance_type' && resolveNameForType(r.variance_type_code)}
                        {col.key === 'detail' && (r.detail ?? '—')}
                        {col.key === 'created_at' && formatDateTime(r.created_at)}
                        {col.key === 'disposition' && resolveNameForDisposition(r.variance_disposition_code)}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-3 py-1.5 text-right">
                      {!r.variance_disposition_code && canChange && (
                        <button onClick={() => setDisposing(r)} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('sfk.variance.dispose', 'Dispose')}</button>
                      )}
                    </td>
                  </tr>
                  {detail.isOpen(r.variance_id) && (
                    <tr>
                      <td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                        <RelatedChildrenPanel groups={buildRelatedGroups(r)} emptyLabel={t('common.related.none', 'No related records.')} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {rows.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center"><EmptyState title={t('common.no_records', 'No records')} /></td></tr>
                )}
              </tbody>
            </table>
          )}
            </PersonalizedGrid>
          </div>
        </ContentCard>
      </div>

      {disposing && (
        <DisposeModal session={session} row={disposing} dispositions={dispositions}
          onClose={() => setDisposing(null)}
          onSaved={() => { setDisposing(null); void reload(); }} />
      )}
    </div>
  );
}

function DisposeModal({ session, row, dispositions, onClose, onSaved }: {
  session: AuthSession; row: VarianceRow; dispositions: CodeOpt[]; onClose: () => void; onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [disposition, setDisposition] = useState(dispositions[0]?.code ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true); setError(null);
    try {
      if (!disposition) throw new Error(t('sfk.variance.err.disposition_required', 'A disposition is required.'));
      const { error: e } = await routedUpdate(session, 'sfk_variance', 'variance_id', row.variance_id, {
        variance_disposition_code: disposition,
        disposed_at: new Date().toISOString(),
        disposed_by: getForensicActorId(session),
      });
      if (e) throw new Error(e.message);
      onSaved();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <Modal
      title={t('sfk.variance.dispose_title', 'Dispose Variance')}
      onClose={onClose}
      width="sm"
      headerAction={<PersonalizationToggle />}
      footer={
        <>
          <button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.cancel', 'Cancel')}</button>
          <button onClick={() => void save()} disabled={busy} className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('common.saving', 'Saving…') : t('common.save', 'Save')}</button>
        </>
      }
    >
      <PersonalizedField componentKey="modal" fieldKey="variance_disposition_code" label={t('sfk.variance.disposition', 'Disposition')}>
        <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('sfk.variance.disposition', 'Disposition')}</label>
        <select value={disposition} onChange={e => setDisposition(e.target.value)} className="mb-3 h-8 w-full rounded border border-csm-border bg-white px-2 text-csm-sm">
          {dispositions.map(d => <option key={d.code} value={d.code}>{d.name}</option>)}
        </select>
      </PersonalizedField>
      {error && <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
    </Modal>
  );
}

export function SfkVariancePage({ session }: { session: AuthSession }) {
  return (
    <PersonalizationProvider pageCode={PAGE_CODE} session={session}>
      <VarianceInner session={session} />
    </PersonalizationProvider>
  );
}
